import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { buscarProductos, unicoProducto } from '../../utils/buscarProductos';
import { ShoppingBag, Plus, Search, X, Truck, FileText, Save, History, Package } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';

const HOST = process.env.REACT_APP_API_URL || 'http://localhost:8000';
const API_COMPRAS = `${HOST}/api/v1/inventario/compras/`;
const API_PROVEEDORES = `${HOST}/api/v1/inventario/proveedores/`;
const API_PRODUCTOS = `${HOST}/api/v1/inventario/productos/`;

export default function ComprasManager() {
    const [view, setView] = useState('list'); // 'list' or 'new'
    const [compras, setCompras] = useState([]);
    const [proveedores, setProveedores] = useState([]);
    const [productos, setProductos] = useState([]);
    const [, setLoading] = useState(false);

    // Form state
    const [selectedProv, setSelectedProv] = useState('');
    const [nroFactura, setNroFactura] = useState('');
    const [observacion, setObservacion] = useState('');
    const [items, setItems] = useState([]); // { producto_id, nombre, cantidad, costo_unitario, subtotal }

    // Search state
    const [prodSearch, setProdSearch] = useState('');
    const [selectedProd, setSelectedProd] = useState(null);
    const [cantTemp, setCantTemp] = useState(1);
    const [costoTemp, setCostoTemp] = useState(0);
    // Precio de venta propuesto para el artículo. Arranca con el que ya
    // tiene, así dejarlo como está es no hacer nada.
    const [ventaTemp, setVentaTemp] = useState(0);
    const cantRef = useRef(null);


    const fetchInitialData = async () => {
        setLoading(true);
        try {
            const [cRes, pRes, prRes] = await Promise.all([
                axios.get(API_COMPRAS),
                axios.get(API_PROVEEDORES),
                axios.get(API_PRODUCTOS)
            ]);
            setCompras(cRes.data);
            setProveedores(pRes.data);
            setProductos(prRes.data);
        } catch (e) {
            console.error(e);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { fetchInitialData(); }, []);

    const selectProducto = (p) => {
        setSelectedProd(p);
        setCostoTemp(p.costo_compra);
        setVentaTemp(p.precio_venta);
        setCantTemp(1);
        setProdSearch('');
        setTimeout(() => cantRef.current?.focus(), 80);
    };

    const handleScannerEnter = (e) => {
        if (e.key !== 'Enter') return;
        e.preventDefault();
        const term = prodSearch.trim();
        if (!term) return;
        const candidato = unicoProducto(productos, term);
        if (candidato) selectProducto(candidato);
    };

    const addItem = () => {
        if (!selectedProd || cantTemp <= 0 || costoTemp < 0) return;

        // Solo se manda el precio de venta si de verdad cambió. Reenviar el
        // mismo valor dejaría una entrada en el historial de precios por cada
        // compra, y el historial dejaría de servir para ver qué cambió.
        const ventaCambio = parseFloat(ventaTemp) !== parseFloat(selectedProd.precio_venta);

        const newItem = {
            producto: selectedProd.id,
            producto_nombre: selectedProd.nombre,
            cantidad: parseInt(cantTemp),
            costo_unitario: parseFloat(costoTemp),
            subtotal: parseInt(cantTemp) * parseFloat(costoTemp),
            precio_venta_anterior: parseFloat(selectedProd.precio_venta),
            ...(ventaCambio ? { precio_venta_nuevo: parseFloat(ventaTemp) } : {}),
        };

        setItems([...items, newItem]);
        setSelectedProd(null);
        setProdSearch('');
        setCantTemp(1);
        setCostoTemp(0);
        setVentaTemp(0);
    };

    const removeItem = (index) => {
        setItems(items.filter((_, i) => i !== index));
    };

    const handleSaveCompra = async () => {
        if (!selectedProv || items.length === 0) {
            alert('Seleccione un proveedor y agregue al menos un producto.');
            return;
        }

        const data = {
            proveedor: selectedProv,
            nro_factura: nroFactura,
            observacion: observacion,
            detalles: items
        };

        try {
            await axios.post(API_COMPRAS, data);
            alert('Compra registrada y stock actualizado.');
            setView('list');
            setItems([]);
            setSelectedProv('');
            setNroFactura('');
            setObservacion('');
            fetchInitialData();
        } catch (e) {
            alert('Error al registrar compra');
        }
    };

    const totalCompra = items.reduce((acc, item) => acc + item.subtotal, 0);

    return (
        <div className="space-y-6">
            {/* Header / Tabs */}
            <div className="flex justify-between items-center bg-white dark:bg-slate-900/60 backdrop-blur-sm p-4 rounded-2xl border border-slate-200 dark:border-white/10">
                <div className="flex gap-2">
                    <button
                        onClick={() => setView('list')}
                        className={`flex items-center gap-2 px-6 py-2.5 rounded-xl font-bold transition-all ${view === 'list' ? 'bg-brand-500 text-negro-950 shadow-lg shadow-brand-500/20' : 'text-slate-500 dark:text-gray-400 hover:bg-slate-100 dark:hover:bg-white/5'}`}
                    >
                        <History size={18} /> Historial
                    </button>
                    <button
                        onClick={() => setView('new')}
                        className={`flex items-center gap-2 px-6 py-2.5 rounded-xl font-bold transition-all ${view === 'new' ? 'bg-brand-500 text-negro-950 shadow-lg shadow-brand-500/20' : 'text-slate-500 dark:text-gray-400 hover:bg-slate-100 dark:hover:bg-white/5'}`}
                    >
                        <Plus size={18} /> Nueva Compra
                    </button>
                </div>

                <div className="hidden md:flex items-center gap-3 pr-4">
                    <div className="bg-brand-500/20 p-2 rounded-lg">
                        <ShoppingBag size={20} className="text-brand-800 dark:text-brand-400" />
                    </div>
                    <span className="text-sm text-slate-500 dark:text-gray-400">Control de Entradas / Compras</span>
                </div>
            </div>

            <AnimatePresence mode="wait">
                {view === 'list' ? (
                    <motion.div
                        key="list"
                        initial={{ opacity: 0, x: -20 }}
                        animate={{ opacity: 1, x: 0 }}
                        exit={{ opacity: 0, x: 20 }}
                        className="bg-white dark:bg-slate-900/60 backdrop-blur-sm border border-slate-200 dark:border-white/10 rounded-2xl overflow-x-auto shadow-xl"
                    >
                        <table className="w-full text-left text-sm min-w-[36rem]">
                            <thead>
                                <tr className="tabla-cabecera">
                                    <th className="px-6 py-4 font-medium text-brand-800 font-bold">ID</th>
                                    <th className="px-6 py-4 font-medium">Fecha</th>
                                    <th className="px-6 py-4 font-medium">Proveedor</th>
                                    <th className="px-6 py-4 font-medium">Nro Factura</th>
                                    <th className="px-6 py-4 font-medium text-right">Monto Total</th>
                                </tr>
                            </thead>
                            <tbody className="tabla-filas">
                                {compras.map(c => (
                                    <tr key={c.id} className="hover:bg-slate-50 dark:hover:bg-white/5 transition-colors group">
                                        <td className="px-6 py-4 font-mono font-bold">#{c.id}</td>
                                        <td className="px-6 py-4 text-slate-600 dark:text-gray-300">
                                            {new Date(c.fecha).toLocaleDateString()}
                                        </td>
                                        <td className="px-6 py-4 text-slate-900 dark:text-white font-medium">{c.proveedor_nombre || 'S/P'}</td>
                                        <td className="px-6 py-4 text-slate-500 dark:text-gray-400">{c.nro_factura || '--'}</td>
                                        <td className="px-6 py-4 text-right font-bold text-exito-400">Gs. {parseFloat(c.total).toLocaleString()}</td>
                                    </tr>
                                ))}
                                {compras.length === 0 && (
                                    <tr>
                                        <td colSpan="5" className="px-6 py-16 text-center text-slate-400 dark:text-gray-500 italics">No hay registros de compras.</td>
                                    </tr>
                                )}
                            </tbody>
                        </table>
                    </motion.div>
                ) : (
                    <motion.div
                        key="new"
                        initial={{ opacity: 0, x: 20 }}
                        animate={{ opacity: 1, x: 0 }}
                        exit={{ opacity: 0, x: -20 }}
                        className="grid grid-cols-1 lg:grid-cols-3 gap-6"
                    >
                        {/* Panel Izquierdo: Formulario y Búsqueda */}
                        <div className="lg:col-span-2 space-y-6">
                            {/* Datos de Cabecera */}
                            <div className="bg-white dark:bg-slate-900/60 backdrop-blur-sm border border-slate-200 dark:border-white/10 p-6 rounded-2xl space-y-4">
                                <h3 className="text-lg font-bold text-slate-900 dark:text-white flex items-center gap-2">
                                    <FileText size={20} className="text-brand-800" /> Datos del Comprobante
                                </h3>
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                    <div>
                                        <label htmlFor="compra-proveedor" className="etiqueta-campo">Proveedor</label>
                                        <select id="compra-proveedor"
                                            className="w-full bg-slate-100 dark:bg-white/5 border border-slate-200 dark:border-white/10 rounded-xl px-4 py-2.5 text-slate-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-brand-500/50"
                                            value={selectedProv}
                                            onChange={e => setSelectedProv(e.target.value)}
                                        >
                                            <option value="">Seleccione un Proveedor</option>
                                            {proveedores.map(p => <option key={p.id} value={p.id}>{p.nombre} ({p.ruc})</option>)}
                                        </select>
                                    </div>
                                    <div>
                                        <label htmlFor="compra-nro-factura" className="etiqueta-campo">Nro Factura</label>
                                        <input id="compra-nro-factura"
                                            placeholder="Ej: 001-001-000123"
                                            className="w-full bg-slate-100 dark:bg-white/5 border border-slate-200 dark:border-white/10 rounded-xl px-4 py-2.5 text-slate-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-brand-500/50"
                                            value={nroFactura}
                                            onChange={e => setNroFactura(e.target.value)}
                                        />
                                    </div>
                                    <div className="md:col-span-2">
                                        <label htmlFor="compra-observaciones" className="etiqueta-campo">Observaciones</label>
                                        <textarea id="compra-observaciones"
                                            placeholder="Detalles adicionales de la compra..."
                                            className="w-full bg-slate-100 dark:bg-white/5 border border-slate-200 dark:border-white/10 rounded-xl px-4 py-2 text-slate-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-brand-500/50"
                                            rows="2"
                                            value={observacion}
                                            onChange={e => setObservacion(e.target.value)}
                                        />
                                    </div>
                                </div>
                            </div>

                            {/* Buscador de Productos */}
                            <div className="bg-white dark:bg-slate-900/60 backdrop-blur-sm border border-slate-200 dark:border-white/10 p-6 rounded-2xl">
                                <h3 className="text-lg font-bold text-slate-900 dark:text-white mb-4 flex items-center gap-2">
                                    <Package size={20} className="text-brand-800" /> Agregar Productos
                                </h3>

                                <div className="space-y-4">
                                    <div className="relative">
                                        <Search className="icono-campo" size={18} />
                                        <input
                                            type="text"
                                            className="w-full bg-slate-100 dark:bg-white/5 border border-slate-200 dark:border-white/10 rounded-xl py-2.5 pl-10 pr-4 text-slate-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-brand-500/50"
                                            value={prodSearch}
                                            onChange={e => setProdSearch(e.target.value)}
                                            onKeyDown={handleScannerEnter}
                                            placeholder="Buscar o escanear código de barras..."
                                        />

                                        {prodSearch && (
                                            <div className="absolute z-10 w-full mt-2 bg-white dark:bg-slate-900 border border-slate-200 dark:border-white/10 rounded-xl shadow-2xl overflow-hidden max-h-60 overflow-y-auto backdrop-blur-xl">
                                                {buscarProductos(productos, prodSearch).map(p => (
                                                    <button
                                                        key={p.id}
                                                        onClick={() => selectProducto(p)}
                                                        className="w-full p-4 flex justify-between items-center hover:bg-slate-50 dark:hover:bg-white/5 border-b border-slate-100 dark:border-white/5 last:border-0 transition-colors"
                                                    >
                                                        <div className="text-left">
                                                            <p className="font-bold text-slate-900 dark:text-white">
                                                                {p.nombre}
                                                                {p.marca && <span className="text-slate-400 dark:text-gray-500 font-normal"> · {p.marca}</span>}
                                                            </p>
                                                            <p className="text-xs text-slate-400 dark:text-gray-500">
                                                                Quedan {p.stock_actual} en stock · Cod: {p.codigo_barras}
                                                            </p>
                                                        </div>
                                                        {/* El importe se rotula. Es el costo —esta es una pantalla de
                                                            compra— y sin decirlo se lee como el precio de venta, que
                                                            es el numero que el dueño tiene en la cabeza. */}
                                                        <div className="text-right shrink-0 pl-3">
                                                            <p className="text-[10px] uppercase tracking-wide text-slate-400 dark:text-gray-500">Último costo</p>
                                                            <p className="text-sm font-mono font-bold text-slate-700 dark:text-slate-200">Gs. {parseFloat(p.costo_compra).toLocaleString('es-PY')}</p>
                                                            <p className="text-[10px] text-slate-400 dark:text-gray-500">venta Gs. {parseFloat(p.precio_venta).toLocaleString('es-PY')}</p>
                                                        </div>
                                                    </button>
                                                ))}
                                            </div>
                                        )}
                                    </div>

                                    {selectedProd && (
                                        <motion.div
                                            initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}
                                            className="flex flex-col md:flex-row gap-4 p-4 pb-7 bg-brand-500/10 border border-brand-500/20 rounded-xl items-end"
                                        >
                                            <div className="flex-1 min-w-0">
                                                <p className="text-xs font-bold text-brand-800 uppercase mb-1">Cargando producto</p>
                                                <p className="text-slate-900 dark:text-white font-bold truncate">{selectedProd.nombre}</p>
                                            </div>

                                            <div className="w-full md:w-28">
                                                <label htmlFor="compra-cantidad" className="etiqueta-simple">Cantidad</label>
                                                <input id="compra-cantidad"
                                                    ref={cantRef}
                                                    type="number"
                                                    min="1"
                                                    className="campo text-center"
                                                    value={cantTemp}
                                                    onChange={e => setCantTemp(e.target.value)}
                                                    onKeyDown={e => e.key === 'Enter' && addItem()}
                                                />
                                            </div>

                                            <div className="w-full md:w-40">
                                                <label htmlFor="compra-costo-unitario-gs" className="etiqueta-simple">Costo unitario</label>
                                                <input id="compra-costo-unitario-gs"
                                                    type="number"
                                                    className="campo text-right font-mono"
                                                    value={costoTemp}
                                                    onChange={e => setCostoTemp(e.target.value)}
                                                    onKeyDown={e => e.key === 'Enter' && addItem()}
                                                />
                                            </div>

                                            {/* El precio de venta, acá mismo.
                                                Antes la compra actualizaba solo el costo: si se
                                                compraba más caro había que acordarse de ir a
                                                Productos a corregir la venta, y hasta que eso
                                                pasara el negocio vendía con menos margen sin que
                                                nada lo avisara.

                                                El margen va posicionado por fuera del flujo: si
                                                ocupara lugar, este campo quedaría más alto que
                                                los otros y la fila se vería torcida. */}
                                            <div className="w-full md:w-44 relative">
                                                <label htmlFor="compra-precio-venta" className="etiqueta-simple">Precio de venta</label>
                                                <input id="compra-precio-venta"
                                                    type="number"
                                                    className="campo text-right font-mono"
                                                    value={ventaTemp}
                                                    onChange={e => setVentaTemp(e.target.value)}
                                                    onKeyDown={e => e.key === 'Enter' && addItem()}
                                                />
                                                <div className="absolute top-full left-0 right-0 mt-1">
                                                    <MargenPropuesto costo={costoTemp} venta={ventaTemp} />
                                                </div>
                                            </div>

                                            <button
                                                onClick={addItem}
                                                title="Agregar a la compra"
                                                className="w-full md:w-auto flex items-center justify-center gap-2 bg-brand-500 hover:bg-brand-400 text-negro-950 font-bold px-4 py-3 rounded-2xl transition-colors shrink-0"
                                            >
                                                <Plus size={20} />
                                                <span className="md:hidden">Agregar</span>
                                            </button>
                                        </motion.div>
                                    )}
                                </div>
                            </div>
                        </div>

                        {/* Panel Derecho: Lista de Items y Guardado */}
                        <div className="space-y-6">
                            <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-white/10 rounded-3xl p-6 shadow-2xl flex flex-col h-full sticky top-6">
                                <div className="flex items-center justify-between mb-6">
                                    <h3 className="titulo-modal">
                                        <ShoppingBag size={24} className="text-brand-800" /> Items
                                    </h3>
                                    <span className="bg-slate-100 dark:bg-white/5 px-3 py-1 rounded-full text-xs text-slate-500 dark:text-gray-400 font-mono">
                                        {items.length} items
                                    </span>
                                </div>

                                <div className="flex-1 space-y-3 overflow-y-auto max-h-[400px] mb-6 pr-2 custom-scrollbar">
                                    {items.map((it, idx) => (
                                        <div key={idx} className="group p-3 bg-slate-50 dark:bg-white/5 border border-slate-100 dark:border-white/5 rounded-2xl hover:border-slate-200 dark:hover:border-white/20 transition-all flex justify-between items-center">
                                            <div>
                                                <p className="text-sm font-bold text-slate-900 dark:text-white">{it.producto_nombre}</p>
                                                <p className="text-xs text-slate-400 dark:text-gray-500">{it.cantidad} un. x Gs. {it.costo_unitario?.toLocaleString()}</p>
                                                {it.precio_venta_nuevo !== undefined && (
                                                    <p className="text-xs text-brand-800 font-semibold">
                                                        Venta: Gs. {it.precio_venta_anterior?.toLocaleString()} → Gs. {it.precio_venta_nuevo.toLocaleString()}
                                                    </p>
                                                )}
                                            </div>
                                            <div className="flex items-center gap-3">
                                                <p className="text-sm font-bold text-brand-800">Gs. {it.subtotal?.toLocaleString()}</p>
                                                <button onClick={() => removeItem(idx)} className="text-slate-400 dark:text-gray-600 hover:text-error-500 transition-colors">
                                                    <X size={16} />
                                                </button>
                                            </div>
                                        </div>
                                    ))}
                                    {items.length === 0 && (
                                        <div className="text-center py-12 text-slate-400 dark:text-gray-600 flex flex-col items-center gap-2">
                                            <Truck size={40} className="opacity-10 mb-2" />
                                            <p className="text-sm italic">Agregue productos comprados</p>
                                        </div>
                                    )}
                                </div>

                                <div className="space-y-4 pt-6 border-t border-slate-200 dark:border-white/10">
                                    <div className="flex justify-between items-end">
                                        <span className="text-sm font-medium text-slate-500 dark:text-gray-400">Total Compra</span>
                                        <span className="text-3xl font-bold bg-gradient-to-r from-slate-900 to-slate-500 dark:from-white dark:to-gray-400 bg-clip-text text-transparent">
                                            Gs. {totalCompra.toLocaleString()}
                                        </span>
                                    </div>
                                    <button
                                        onClick={handleSaveCompra}
                                        disabled={items.length === 0 || !selectedProv}
                                        className={`w-full flex items-center justify-center gap-3 py-4 rounded-2xl font-bold text-lg transition-all active:scale-95 ${items.length > 0 && selectedProv ? 'bg-brand-500 hover:bg-brand-600 text-negro-950 shadow-xl shadow-brand-500/30' : 'bg-slate-100 dark:bg-white/5 text-slate-400 dark:text-gray-600 cursor-not-allowed border border-slate-200 dark:border-white/5'}`}
                                    >
                                        <Save size={24} /> Registrar Entrada
                                    </button>
                                </div>
                            </div>
                        </div>
                    </motion.div>
                )}
            </AnimatePresence>
        </div>
    );
}


/**
 * El margen que deja el precio propuesto, mientras se escribe.
 *
 * Es el dato que decide si el precio está bien, y hacerlo a mano en el
 * mostrador con una compra de veinte renglones no lo hace nadie. Avisa en rojo
 * cuando se vende por debajo del costo, que es el error que esta pantalla
 * existe para evitar.
 */
function MargenPropuesto({ costo, venta }) {
    const c = parseFloat(costo);
    const v = parseFloat(venta);
    if (!c || !v || c <= 0) return null;

    const margen = ((v - c) / c) * 100;
    const perdida = v < c;

    return (
        <p className={`text-[10px] mt-1 font-bold ${perdida ? 'text-error-400' : 'text-slate-400 dark:text-gray-500'}`}>
            {perdida ? '¡Por debajo del costo!' : `Margen ${margen.toFixed(1)}%`}
        </p>
    );
}
